import { MAX_LEVELS, MIN_LEVELS, type Rubric, type RubricItem, type RubricLevel } from './rubric';

/** Level names and positions are shared; descriptions belong to each criterion. */
export function renameLevel(rubric: Rubric, index: number, name: string): Rubric {
  return { ...rubric, items: rubric.items.map(item => ({ ...item,
    levels: item.levels.map((level, i) => i === index ? { ...level, name } : level),
  })) };
}

export function addLevel(rubric: Rubric): Rubric {
  const count = rubric.items[0]?.levels.length ?? 0;
  if (count >= MAX_LEVELS) return rubric;
  return { ...rubric, items: rubric.items.map(item => ({ ...item,
    levels: [...item.levels, { name: `Level ${count + 1}`, text: '' }],
  })) };
}

export function removeLevel(rubric: Rubric, index: number): Rubric {
  const count = rubric.items[0]?.levels.length ?? 0;
  if (count <= MIN_LEVELS || index < 0 || index >= count) return rubric;
  return { ...rubric,
    // Preserve the selected threshold when an earlier level is removed. When the
    // threshold itself is removed, use its successor (or the new highest level).
    passLevel: Math.min(count - 2, rubric.passLevel - (index < rubric.passLevel ? 1 : 0)),
    items: rubric.items.map(item => ({ ...item, levels: item.levels.filter((_, i) => i !== index) })),
  };
}

/** Existing locally saved rubrics may have different scales per criterion. */
export function hasSharedLevels(rubric: Rubric): boolean {
  const names = rubric.items[0]?.levels.map(level => level.name) ?? [];
  return rubric.items.every(item => item.levels.length === names.length && item.levels.every((level, i) => level.name === names[i]));
}

/** Explicit migration keeps descriptions at their existing ordinal position. */
export function alignLevels(rubric: Rubric): Rubric {
  const levels = rubric.items[0]?.levels ?? [];
  return { ...rubric, passLevel: Math.max(0, Math.min(rubric.passLevel, levels.length - 1)),
    items: rubric.items.map(item => ({ ...item,
      levels: levels.map((level, i) => ({ name: level.name, text: item.levels[i]?.text ?? '' })),
    })),
  };
}

/** Level descriptions are optional. A blank one is scored by its name, which is weaker but honest. */
export const levelText = (item: RubricItem, level: RubricLevel): string =>
  level.text.trim() || `${level.name} work on ${item.name || 'this criterion'}.`;

export const blankLevelCount = (rubric: Rubric): number =>
  rubric.items.reduce((n, item) => n + item.levels.filter(l => !l.text.trim()).length, 0);
